import type { Rng } from '../engine/rng';
import { getSpecies, type SpeciesDef } from './data/species';

/**
 * Istanza concreta di un Lumine (l'equivalente di un "esemplare" catturato
 * o selvatico), separata dalla definizione di specie. Struttura serializzabile:
 * finisce così com'è nel salvataggio e, in Fase 2, nei messaggi di scambio.
 */

export interface Stats {
  hp: number;
  atk: number;
  def: number;
  spd: number;
}

export interface LumineInstance {
  uid: string;
  speciesId: string;
  level: number;
  xp: number;
  hp: number; // HP correnti
  moves: string[]; // max 4 mosse
}

export function statsFor(species: SpeciesDef, level: number): Stats {
  const b = species.baseStats;
  return {
    hp: b.hp + level * 3,
    atk: Math.floor(b.atk * (1 + level * 0.06)),
    def: Math.floor(b.def * (1 + level * 0.06)),
    spd: Math.floor(b.spd * (1 + level * 0.06)),
  };
}

export function xpForLevel(level: number): number {
  return level * level * 18;
}

export function movesAtLevel(species: SpeciesDef, level: number): string[] {
  return species.learnset
    .filter((entry) => entry.level <= level)
    .map((entry) => entry.moveId)
    .slice(-4);
}

let uidCounter = 0;

export function createLumine(speciesId: string, level: number, rng?: Rng): LumineInstance {
  const species = getSpecies(speciesId);
  const suffix = rng ? Math.floor(rng.next() * 1e9).toString(36) : (++uidCounter).toString(36);
  return {
    uid: `${speciesId}-${Date.now().toString(36)}-${suffix}`,
    speciesId,
    level,
    xp: xpForLevel(level),
    hp: statsFor(species, level).hp,
    moves: movesAtLevel(species, level),
  };
}

export interface LevelUpResult {
  leveledUp: boolean;
  newLevel: number;
  learnedMoves: string[];
}

/** Applica xp guadagnati; gestisce level-up multipli e nuove mosse. */
export function grantXp(lumine: LumineInstance, amount: number): LevelUpResult {
  const species = getSpecies(lumine.speciesId);
  const startLevel = lumine.level;
  lumine.xp += amount;
  const learnedMoves: string[] = [];
  while (lumine.xp >= xpForLevel(lumine.level + 1) && lumine.level < 100) {
    lumine.level += 1;
    const oldMax = statsFor(species, lumine.level - 1).hp;
    const newMax = statsFor(species, lumine.level).hp;
    lumine.hp = Math.min(newMax, lumine.hp + (newMax - oldMax));
    for (const entry of species.learnset) {
      if (entry.level === lumine.level && !lumine.moves.includes(entry.moveId)) {
        lumine.moves = [...lumine.moves, entry.moveId].slice(-4);
        learnedMoves.push(entry.moveId);
      }
    }
  }
  return { leveledUp: lumine.level > startLevel, newLevel: lumine.level, learnedMoves };
}

export function maxHp(lumine: LumineInstance): number {
  return statsFor(getSpecies(lumine.speciesId), lumine.level).hp;
}

export function isFainted(lumine: LumineInstance): boolean {
  return lumine.hp <= 0;
}

export function healFull(lumine: LumineInstance): void {
  lumine.hp = maxHp(lumine);
}
